import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { EmptyState } from '@/components/admin/shared/EmptyState';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { useBroadcastsQuery } from '@/hooks/useBroadcasts';
import type { BroadcastChannels, BroadcastStatus } from '@/api/broadcasts/broadcastsApi.types';

const STATUS_TONE: Record<BroadcastStatus, 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  scheduled: 'warning',
  sent: 'success',
  cancelled: 'neutral',
};

const CHANNEL_BADGE_ORDER: { channelKey: keyof BroadcastChannels; labelKey: string }[] = [
  { channelKey: 'inApp', labelKey: 'detail.channelInApp' },
  { channelKey: 'email', labelKey: 'detail.channelEmail' },
  { channelKey: 'sms', labelKey: 'detail.channelSms' },
];

export function BroadcastsPage() {
  const { t } = useTranslation('broadcasts');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const broadcastsQuery = useBroadcastsQuery(page);
  const broadcasts = broadcastsQuery.data === undefined ? [] : broadcastsQuery.data.items;
  const totalItems = broadcastsQuery.data === undefined ? 0 : broadcastsQuery.data.totalItems;

  return (
    <PageContainer width="full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zooindigo">{t('list.title')}</h1>
          <p className="text-sm text-zoomuted mt-1">{t('list.subtitle')}</p>
        </div>
        <Button
          type="button"
          onClick={function openCompose() {
            navigate(APPLICATION_ROUTES.workspaceBroadcastCompose);
          }}
        >
          <Icon name="Megaphone" />
          {t('list.new')}
        </Button>
      </div>

      {broadcastsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : broadcasts.length === 0 ? (
        <EmptyState
          iconName="Megaphone"
          title={t('list.empty')}
          description={t('list.emptyHint')}
        />
      ) : (
        <Card padding="none" className="overflow-hidden">
          <ul className="divide-y divide-zooborder">
            {broadcasts.map(function renderBroadcast(broadcast) {
              return (
                <li key={broadcast.id}>
                  <button
                    type="button"
                    onClick={function openDetail() {
                      navigate(
                        APPLICATION_ROUTES.workspaceBroadcastDetail.replace(
                          ':broadcastId',
                          broadcast.id
                        )
                      );
                    }}
                    className="w-full flex flex-col gap-2 px-5 py-4 text-left hover:bg-zoovpalelavender hover:bg-opacity-40 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-zooindigo truncate">{broadcast.title}</p>
                      <p className="text-xs text-zoomuted mt-0.5">
                        {broadcast.status === 'scheduled' && broadcast.scheduledFor !== null
                          ? t('detail.scheduledFor', {
                              date: formatIsoDateTimeToTimestamp(broadcast.scheduledFor),
                            })
                          : formatIsoDateTimeToTimestamp(broadcast.createdAt)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {CHANNEL_BADGE_ORDER.filter(function isActiveChannel(entry) {
                        return broadcast.channels[entry.channelKey] === true;
                      }).map(function renderChannelBadge(entry) {
                        return (
                          <Badge key={entry.channelKey} tone="neutral" outline size="sm">
                            {t(entry.labelKey)}
                          </Badge>
                        );
                      })}
                      <span className="text-xs text-zoomuted">
                        {t('list.recipients', { count: broadcast.totalRecipients })}
                      </span>
                      <Badge tone={STATUS_TONE[broadcast.status]} size="sm">
                        {t('status.' + broadcast.status)}
                      </Badge>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {totalItems > ADMIN_LIST_PAGE_SIZE ? (
            <div className="px-5 py-3 border-t border-zooborder">
              <ListPagination
                page={page}
                pageSize={ADMIN_LIST_PAGE_SIZE}
                totalItems={totalItems}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </Card>
      )}
    </PageContainer>
  );
}
